import { useState, useContext, useEffect } from 'react'
import _ from "lodash"

import { TextField, Typography, Tooltip, Button, FormControlLabel, FormLabel, RadioGroup, Radio } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'

import { PassContext } from '../pass-context'
import dataSelector from './data-selector'
import PopupShell from '../popup-shell'

const PopupDialogText = ({ open, close }) => {

  const { template, setTemplate, instanceData, setInstanceData, syncBarcodeData } = useContext(PassContext)

  const [newInstanceData, setNewInstanceData] = useState({})
  const [barcode, setBarcode] = useState()

  // Runs on "open" state change or if template / instance data change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    try {
      if (open) {
        setNewInstanceData(_.cloneDeep(instanceData))

        if (template.barcode) {
          const initialBarcode = _.get(template, "barcode")
          setBarcode(initialBarcode)
        }
      }
    }
    catch (err) {
      throw err
    }
  }, [open, template.barcode])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const saveFormData = event => {
    event.preventDefault() // Prevents a "submit" which reloads the page

    // Save content of our "new" template/instance data back to state
    if (!_.isEqual(newInstanceData, instanceData)) setInstanceData(newInstanceData)

    if (!_.isEqual(barcode, template.barcode)) {

      let nt = _.cloneDeep(template)

      if (barcode) nt.barcode = barcode
      else delete nt.barcode

      setTemplate(nt)

      syncBarcodeData({ source: "apple", barcode: barcode })
    }
    close()
  }

  const handleChangeBarcodeType = value => {
    if (value !== "NONE") setBarcode({
      "altText": "{{__barcode__.__text__}}",
      "format": value,
      "message": "{{__barcode__.__value__}}"
    })
    else setBarcode(null)
  }

  const instanceFields = {
    message : "__barcode__.__value__",
    altText : "__barcode__.__text__"
  }

  const updateBarcode = (field, value) => {
    setNewInstanceData((prev) => { 
      let t = {...prev}
      _.set(t,instanceFields[field],value)
      return t
    })
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    return (
      <PopupShell open={open} onClose={handleClose}>

        <div className="flex flex-col p-2 h-full w-full overflow-x-hidden">

          <div className="flex flex-row justify-between mt-5 w-full">
            <Typography color="secondary" variant="h6">Barcode</Typography>
            <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
          </div>

          <RadioGroup
            value={barcode?.format ?? "NONE"}
            onChange={e => handleChangeBarcodeType(e.target.value)}
          >
            <FormControlLabel value="NONE" control={<Radio />} label="No barcode" />
            <FormControlLabel value="PKBarcodeFormatAztec" control={<Radio />} label="Aztec" />
            <FormControlLabel value="PKBarcodeFormatQR" control={<Radio />} label="QR Code" />
            <FormControlLabel value="PKBarcodeFormatPDF417" control={<Radio />} label="PDF 417" />
            <FormControlLabel value="PKBarcodeFormatCode128" control={<Radio />} label="Code 128" />
          </RadioGroup>

          <div className='flex flex-col gap-2'>
            <FormLabel id="barcode_type_label">Barcode Value (message)</FormLabel>
            <DataSourceData barcode={barcode} newInstanceData={newInstanceData} field="message" updateBarcode={updateBarcode} />
          </div>

          <div className='flex flex-col gap-2'>
            <FormLabel id="barcode_type_label">Barcode Serial Number (altText)</FormLabel>
            <DataSourceData barcode={barcode} newInstanceData={newInstanceData} field="altText" updateBarcode={updateBarcode} />
          </div>

          <div className='fixed bottom-5 w-[460px]'>

            <hr className='mb-5' />

            <div className="flex flex-row w-full justify-end">
              <Button variant="contained" onClick={saveFormData}>Save</Button>
            </div>
          </div>
        </div>
      </PopupShell >
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogText

//////////////////////////////////////////////////////////////////////////////////////////////////////////
// Controlled JSX (Must have values passes as paramets and not access them from the parent 
// - hence outside the main function). Otherwise they do not update when data changes!
//////////////////////////////////////////////////////////////////////////////////////////////////////////

const DataSourceData = ({ barcode, newInstanceData, field, updateBarcode }) => {

  const resolvedInstanceValue = dataSelector({ template: barcode, instanceData: newInstanceData, path: field })

  return (
    <TextField
      value={barcode ? resolvedInstanceValue : ""}
      type="search"
      disabled={!barcode}
      onChange={(event) => { updateBarcode(field, event.target.value) }}
      multiline={field === "message"}
      maxRows={10}
      fullWidth
    />

  )
}
